import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Patient } from "./PatientCard";
import PatientEditForm from "./PatientEditForm";
import PatientLabOrdersTab from "./Tabs/LabRequisition/PatientLabOrdersTab";
import PatientDocumentsTab from "./Tabs/Documents/PatientDocumentsTab";
import PatientTreatmentPlansTab from "./Tabs/TreatmentPlan/PatientTreatmentPlansTab";
import PatientVitalSignsTab from "./Tabs/Vital/PatientVitalSignsTab";
import PatientPrescriptionsTab from "./Tabs/Prescription/PatientPrescriptionsTab";
import PatientAppointmentHistoryTab from "./Tabs/Appointment/PatientAppointmentHistoryTab";
import PatientBodyMapTab from "./Tabs/BodyMap/BodyMapTab";
import PatientOverviewTab from "./Tabs/PatientOverview/PatientOverviewTab";
import { useAuth } from "../../hooks/useAuth";
import PatientComplaintsTab from "./Tabs/Patientcomplaints/Patientcomplaintstab";

interface PatientDetailsModalProps {
  patient: Patient | null;
  isOpen: boolean;
  onClose: () => void;
  onPatientUpdated?: (updatedPatient: Patient) => void;
  initialTab?: string;
}

type TabType =
  | "overview"
  | "vital-signs"
  | "lab-orders"
  | "body-mapping"
  | "treatment-plans"
  | "documents"
  | "prescriptions"
  | "appointments"
  | "complaints"
  | "edit";

const PatientDetailsModal: React.FC<PatientDetailsModalProps> = ({
  patient,
  isOpen,
  onClose,
  onPatientUpdated,
  initialTab = "overview",
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabType>(initialTab as TabType);
  const [screenSize, setScreenSize] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    width: 0,
    height: 0,
  });

  const isPhysicalTherapist = user?.specialty
    ?.toLowerCase()
    .includes("physical therapy");

  // ✅ Update active tab when initialTab changes (from URL)
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab as TabType);
    }
  }, [initialTab]);

  // Enhanced screen size detection
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setScreenSize({
        isMobile: width < 640,
        isTablet: width >= 640 && width < 1024,
        isDesktop: width >= 1024,
        width,
        height,
      });
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // ✅ Handle tab change with clean URL navigation
  const handleTabChange = (newTab: TabType) => {
    setActiveTab(newTab);

    if (patient && isOpen) {
      // Navigate to: /patients/123/vital-signs
      navigate(`/patients/${patient.id}/${newTab}`, { replace: false });
    }
  };

  // ✅ Enhanced close handler
  const handleClose = () => {
    setActiveTab(initialTab as TabType);
    onClose();
    // Parent component handles navigation to /patients
  };

  const handlePatientUpdated = (updatedPatient: Patient) => {
    if (onPatientUpdated) {
      onPatientUpdated(updatedPatient);
    }
    handleTabChange("overview");
  };

  if (!patient) return null;

  const { isMobile, isTablet, isDesktop, width, height } = screenSize;

  // Define all tabs
  const allTabs = [
    { id: "overview", label: "Overview", icon: "👁️", shortLabel: "Overview" },
    {
      id: "vital-signs",
      label: "Vital Signs",
      icon: "🩺",
      shortLabel: "Vitals",
    },
    {
      id: "complaints",
      label: "Complaints",
      icon: "💬",
      shortLabel: "Complaints",
    },
    { id: "lab-orders", label: "Lab Orders", icon: "🧪", shortLabel: "Labs" },
    {
      id: "body-mapping",
      label: "Body Map",
      icon: "🗺️",
      shortLabel: "Body Map",
    },
    {
      id: "treatment-plans",
      label: "Treatment Plans",
      icon: "📋",
      shortLabel: "Treatment",
    },
    { id: "documents", label: "Documents", icon: "📄", shortLabel: "Docs" },
    {
      id: "prescriptions",
      label: "Prescriptions",
      icon: "💊",
      shortLabel: "Rx",
    },
    {
      id: "appointments",
      label: "Appointments",
      icon: "📅",
      shortLabel: "Appts",
    },
  ];

  // Filter tabs based on user specialty
  const tabs = allTabs.filter((tab) => {
    if (isPhysicalTherapist) {
      const excludedTabs = [
        "lab-orders",
        "body-mapping",
        "prescriptions",
        "treatment-plans",
      ];
      return !excludedTabs.includes(tab.id);
    }
    return true;
  });

  // Enhanced overlay style for 90% coverage
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    zIndex: 1000,
    opacity: isOpen ? 1 : 0,
    visibility: isOpen ? "visible" : "hidden",
    transition: "opacity 0.3s ease, visibility 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "0" : "5vh 5vw",
  };

  // Enhanced modal style for 90% screen coverage
  const getModalStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      backgroundColor: "#ffffff",
      boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
      transform: isOpen ? "scale(1)" : "scale(0.95)",
      transition: "transform 0.3s ease",
      zIndex: 1001,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      position: "relative",
    };

    if (isMobile) {
      return {
        ...baseStyle,
        width: "100%",
        height: "100%",
        borderRadius: "0",
        transform: isOpen ? "translateY(0)" : "translateY(100%)",
        transition: "transform 0.3s ease",
      };
    }

    if (isTablet) {
      return {
        ...baseStyle,
        width: "90%",
        height: "90%",
        maxWidth: "90vw",
        maxHeight: "90vh",
        borderRadius: "12px",
      };
    }

    return {
      ...baseStyle,
      width: "90%",
      height: "90%",
      maxWidth: "90vw",
      maxHeight: "90vh",
      minWidth: "800px",
      minHeight: "600px",
      borderRadius: "16px",
    };
  };

  const getHeaderStyle = (): React.CSSProperties => ({
    padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: isMobile ? "flex-start" : "center",
    flexShrink: 0,
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "16px" : isTablet ? "12px" : "0",
    minHeight: isMobile ? "auto" : "80px",
  });

  const getTitleStyle = (): React.CSSProperties => ({
    fontSize: isMobile
      ? width < 375
        ? "18px"
        : "20px"
      : isTablet
      ? "22px"
      : "24px",
    fontWeight: "700",
    color: "#1e293b",
    margin: 0,
    lineHeight: "1.2",
    wordBreak: "break-word",
  });

  const getSubtitleStyle = (): React.CSSProperties => ({
    fontSize: isMobile
      ? width < 375
        ? "11px"
        : "12px"
      : isTablet
      ? "13px"
      : "14px",
    color: "#64748b",
    marginTop: "4px",
    lineHeight: "1.4",
    wordBreak: "break-word",
  });

  const getTabsContainerStyle = (): React.CSSProperties => ({
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#ffffff",
    padding: isMobile ? "0 12px" : isTablet ? "0 20px" : "0 24px",
    display: "flex",
    alignItems: "center",
    overflowX: "auto",
    overflowY: "hidden",
    flexShrink: 0,
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    justifyContent: "flex-start",
    flexWrap: "nowrap",
    width: "100%",
    minHeight: isMobile ? "56px" : "60px",
    WebkitOverflowScrolling: "touch",
  });

  const getTabStyle = (isActive: boolean): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      borderBottom: isActive ? "3px solid #3b82f6" : "3px solid transparent",
      color: isActive ? "#3b82f6" : "#64748b",
      fontWeight: isActive ? "600" : "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      whiteSpace: "nowrap",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      userSelect: "none",
      WebkitTapHighlightColor: "transparent",
    };

    if (isMobile) {
      return {
        ...baseStyle,
        padding: width < 375 ? "12px 8px" : "12px 12px",
        fontSize: width < 375 ? "18px" : "20px",
        gap: "0",
        minWidth: "44px",
        flexDirection: "column",
      };
    }

    if (isTablet) {
      return {
        ...baseStyle,
        padding: "12px 16px",
        fontSize: "13px",
        gap: "6px",
        minWidth: "fit-content",
      };
    }

    return {
      ...baseStyle,
      padding: "12px 20px",
      fontSize: "14px",
      gap: "8px",
      minWidth: "fit-content",
    };
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: "auto",
    backgroundColor: "#f8fafc",
    WebkitOverflowScrolling: "touch",
    position: "relative",
    height: "100%",
  };

  const getButtonStyle = (
    variant: "primary" | "secondary" = "secondary"
  ): React.CSSProperties => ({
    padding: isMobile ? "12px 16px" : isTablet ? "10px 16px" : "8px 16px",
    borderRadius: isMobile ? "10px" : "8px",
    border: "none",
    fontSize: isMobile ? "14px" : "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: variant === "primary" ? "#3b82f6" : "#e5e7eb",
    color: variant === "primary" ? "white" : "#374151",
    minHeight: isMobile ? "48px" : "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: isMobile ? "8px" : "6px",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    flex: isMobile ? "1" : "none",
  });

  const getButtonContainerStyle = (): React.CSSProperties => ({
    display: "flex",
    gap: isMobile ? "12px" : "8px",
    width: isMobile ? "100%" : "auto",
    flexDirection: "row",
    alignItems: "center",
  });

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return <PatientOverviewTab patient={patient} />;
      case "edit":
        return (
          <PatientEditForm
            patient={patient}
            onSave={handlePatientUpdated}
            onCancel={() => handleTabChange("overview")}
          />
        );
      case "vital-signs":
        return <PatientVitalSignsTab patient={patient} />;
      case "complaints":
        return <PatientComplaintsTab patientId={patient.id} />;
      case "lab-orders":
        return <PatientLabOrdersTab patient={patient} />;
      case "body-mapping":
        return <PatientBodyMapTab patient={patient} />;
      case "treatment-plans":
        return <PatientTreatmentPlansTab patient={patient} />;
      case "documents":
        return <PatientDocumentsTab patient={patient} />;
      case "prescriptions":
        return <PatientPrescriptionsTab patient={patient} />;
      case "appointments":
        return <PatientAppointmentHistoryTab patient={patient} />;
      default:
        return <PatientOverviewTab patient={patient} />;
    }
  };

  return (
    <>
      <style>
        {`
          .tabs-container::-webkit-scrollbar {
            display: none;
          }
          
          .tabs-container {
            flex-direction: row !important;
            scroll-behavior: smooth;
          }
          
          .tab-item:focus {
            outline: 2px solid #3b82f6;
            outline-offset: 2px;
          }
          
          .modal-button:focus {
            outline: 2px solid #3b82f6;
            outline-offset: 2px;
          }
          
          .tab-item {
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
          }
          
          @media (max-width: 374px) {
            .patient-name { font-size: 16px !important; }
            .patient-subtitle { font-size: 10px !important; }
          }
          
          @media (min-width: 375px) and (max-width: 639px) {
            .patient-name { font-size: 18px !important; }
            .patient-subtitle { font-size: 11px !important; }
          }
          
          @media (min-width: 640px) and (max-width: 767px) {
            .patient-name { font-size: 20px !important; }
            .patient-subtitle { font-size: 12px !important; }
          }
          
          @media (min-width: 768px) and (max-width: 1023px) {
            .patient-name { font-size: 22px !important; }
            .patient-subtitle { font-size: 13px !important; }
          }
          
          @media (min-width: 1024px) {
            .patient-name { font-size: 24px !important; }
            .patient-subtitle { font-size: 14px !important; }
          }
          
          @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
            .modal-button { font-weight: 500; }
          }
          
          @media (prefers-reduced-motion: reduce) {
            * {
              transition-duration: 0.01ms !important;
              animation-duration: 0.01ms !important;
            }
          }
          
          @media (prefers-color-scheme: dark) {
            .modal-overlay { background-color: rgba(0, 0, 0, 0.8); }
          }
          
          .modal-content::-webkit-scrollbar { width: 8px; }
          .modal-content::-webkit-scrollbar-track { background: #f1f5f9; }
          .modal-content::-webkit-scrollbar-thumb { 
            background: #cbd5e1; 
            border-radius: 4px; 
          }
          .modal-content::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        `}
      </style>

      <div style={overlayStyle} className="modal-overlay" onClick={handleClose}>
        <div style={getModalStyle()} onClick={(e) => e.stopPropagation()}>
          <div style={getHeaderStyle()} className="modal-header">
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={getTitleStyle()} className="patient-name">
                {patient.full_name ||
                  `${patient.first_name} ${patient.last_name}`}
              </h2>
              <div style={getSubtitleStyle()} className="patient-subtitle">
                {patient.age} years old • {patient.gender} • {patient.phone}
              </div>
            </div>
            <div style={getButtonContainerStyle()} className="nav-controls">
              {activeTab !== "edit" && (
                <button
                  className="modal-button"
                  style={getButtonStyle("primary")}
                  onClick={() => handleTabChange("edit")}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#2563eb")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "#3b82f6")
                  }
                  aria-label="Edit Patient"
                >
                  <span>✏️</span>
                  {!isMobile && <span>Edit Patient</span>}
                </button>
              )}
              <button
                className="modal-button"
                style={getButtonStyle("secondary")}
                onClick={handleClose}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#d1d5db")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#e5e7eb")
                }
                aria-label="Close Modal"
              >
                <span>✕</span>
                {!isMobile && <span>Close</span>}
              </button>
            </div>
          </div>

          {activeTab !== "edit" && (
            <div
              style={getTabsContainerStyle()}
              className="tabs-container"
              role="tablist"
              aria-label="Patient information tabs"
            >
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  className="tab-item"
                  style={getTabStyle(activeTab === tab.id)}
                  onClick={() => handleTabChange(tab.id as TabType)}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.color = "#374151";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.color = "#64748b";
                    }
                  }}
                  role="tab"
                  tabIndex={0}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`panel-${tab.id}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleTabChange(tab.id as TabType);
                    }
                  }}
                >
                  <span role="img" aria-label={tab.label}>
                    {tab.icon}
                  </span>
                  {!isMobile && (
                    <span>{isTablet ? tab.shortLabel : tab.label}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          <div
            style={contentStyle}
            className="modal-content"
            role="tabpanel"
            id={`panel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
          >
            {renderTabContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default PatientDetailsModal;
